import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useAppDispatch } from '../../../../redux/hooks';
import {
  dataModelerAddDraftDerivedField,
  dataModelerSetUiDraftDerivedField,
  dataModelerUpdateDraftDerivedField,
  dataModelerUpdateExistingFieldInput,
  IDerivedFieldDefinitionDraft,
} from '../../../../redux/slices/dataModeler';
import {
  FieldError,
  FieldLabel,
  InputField,
  TextAreaField,
  VerticalField,
} from '../../../../components/Form';
import {
  rawSqlValidator,
  useViewColumnsValidator,
  validationErrorToString,
} from '../validation';
import { UnderTableHeaderDiv } from '../DataModelerCommonUI';
import produce from 'immer';
import { SingleSelectMenu } from '../../../../components/Dropdown';
import { FIELD_TYPE_OPTIONS } from '../utils';
import {
  FieldType,
  IFieldTypeParamsFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import DerivedColumnTimestampTzInput from '../DataModelerDerivedColumn/DerivedColumnTimestampTzInput';
import DerivedColumnArrayItemTypeInput from '../DataModelerDerivedColumn/DerivedColumnArrayItemTypeInput';
import { batch } from 'react-redux';
import {
  DERIVED_FIELD_EDIT_FORM_ANCHOR_ID,
  newDraftDerivedFieldToInput,
} from './utils';
import TickCheckbox from '../../../../components/Checkbox';
import { PrimaryButton, SecondaryButton } from '../../../../components/Button';
import { level1Content } from '../../../../styles/shareableStyles';
import KubitTooltip from '../../../../components/KubitTooltip';

interface IProps {
  draft: IDerivedFieldDefinitionDraft;
}

const FormWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 8px 8px;
  ${level1Content};
`;

const FormRow = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 8px;
`;

export const StyledTypeSelectMenu = styled(SingleSelectMenu)`
  width: 160px;
`;

const FormCell = styled(VerticalField)<{ $size: 'md' | 'lg' }>`
  margin-bottom: 0;
  width: ${({ $size }) => ($size === 'lg' ? 680 : 170)}px;
`;

const ExpressionWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  column-gap: 8px;
`;

const CheckboxWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  width: 150px;
  align-self: flex-end;
  // reset semantic styles
  .ui.checkbox label,
  .ui.checkbox + label {
    color: inherit;
  }
`;

const DataModelerEditDerivedField = ({ draft }: IProps) => {
  const dispatch = useAppDispatch();
  const [isDirty, setIsDirty] = useState(false);

  const { namesValidator, allOtherColumnsSet } = useViewColumnsValidator();

  const removeNewDraftField = () => {
    dispatch(dataModelerSetUiDraftDerivedField({ draft: null }));
  };

  const canEditMeta = !draft.fieldMeta && !draft.newFieldKey;

  const fieldNameError =
    canEditMeta &&
    namesValidator.safeParse({ value: draft.fieldName }).error?.errors[0]
      ?.message;
  const headerName = canEditMeta ? 'Add' : 'Edit';

  const updateDraftFieldProp = (
    input: Partial<IDerivedFieldDefinitionDraft>,
  ) => {
    const newDraft = produce(draft, (old: IDerivedFieldDefinitionDraft) => {
      return {
        ...old,
        ...input,
      };
    });
    dispatch(dataModelerSetUiDraftDerivedField({ draft: newDraft }));
  };

  const setTypeParams = (params: IFieldTypeParamsFieldsFragment) => {
    updateDraftFieldProp({ typeParams: params });
  };

  const setRawSql = (rawSql: string) => {
    const newDraft = produce(draft, (old: IDerivedFieldDefinitionDraft) => {
      return {
        ...old,
        rawSql: {
          rawSql,
          fullDataRequired: old.rawSql.fullDataRequired,
        },
      };
    });
    setIsDirty(true);
    dispatch(dataModelerSetUiDraftDerivedField({ draft: newDraft }));
  };

  const onFullDataClick = () => {
    const newDraft = produce(draft, (old: IDerivedFieldDefinitionDraft) => {
      return {
        ...old,
        rawSql: {
          rawSql: old.rawSql.rawSql,
          fullDataRequired: !old.rawSql.fullDataRequired,
        },
      };
    });
    dispatch(dataModelerSetUiDraftDerivedField({ draft: newDraft }));
  };

  const validationError = useMemo(() => {
    const parsed = rawSqlValidator.safeParse(draft.rawSql.rawSql);
    if (parsed.success) {
      return null;
    } else {
      return validationErrorToString(parsed.error);
    }
  }, [draft.rawSql.rawSql]);

  const canConfirm = !fieldNameError && !validationError;

  const handleConfirm = () => {
    batch(() => {
      if (draft.fieldMeta) {
        dispatch(
          dataModelerUpdateExistingFieldInput({
            fieldMeta: draft.fieldMeta,
            input: {
              key: draft.fieldMeta.key,
              derivedDefinition: {
                rawSql: draft.rawSql,
              },
            },
          }),
        );
      } else if (draft.newFieldKey) {
        dispatch(
          dataModelerUpdateDraftDerivedField({
            fieldKey: draft.newFieldKey,
            input: { rawSql: draft.rawSql },
          }),
        );
      } else {
        dispatch(
          dataModelerAddDraftDerivedField({
            input: newDraftDerivedFieldToInput(draft, allOtherColumnsSet),
          }),
        );
      }
      dispatch(dataModelerSetUiDraftDerivedField({ draft: null }));
    });
  };

  return (
    <FormWrapper>
      <a id={DERIVED_FIELD_EDIT_FORM_ANCHOR_ID} />
      <FormRow>
        <UnderTableHeaderDiv>{headerName} Derived Field</UnderTableHeaderDiv>
      </FormRow>
      <FormRow>
        <FormCell $size={'md'}>
          <FieldLabel>Field Name</FieldLabel>
          <InputField
            autoComplete="off"
            name="columnName"
            value={draft.fieldName}
            onChange={(newName) => updateDraftFieldProp({ fieldName: newName })}
            placeholder={'Field name'}
            hasError={!!fieldNameError}
            disabled={!canEditMeta}
          />
        </FormCell>
        <FormCell $size={'md'}>
          <FieldLabel>Type</FieldLabel>
          <StyledTypeSelectMenu
            options={FIELD_TYPE_OPTIONS}
            value={draft.fieldType}
            onChange={(s) =>
              updateDraftFieldProp({ fieldType: s as FieldType })
            }
            disabled={!canEditMeta}
          />
        </FormCell>
        {draft.fieldType === FieldType.Timestamp && !!draft.typeParams && (
          <FormCell $size={'md'}>
            <DerivedColumnTimestampTzInput
              manualSelectColumnTypeParams={draft.typeParams}
              setManualSelectColumnTypeParams={setTypeParams}
              readOnly={!canEditMeta}
            />
          </FormCell>
        )}
        {draft.fieldType === FieldType.Array && !!draft.typeParams && (
          <FormCell $size={'md'}>
            <DerivedColumnArrayItemTypeInput
              typeParams={draft.typeParams}
              setTypeParams={setTypeParams}
              readOnly={!canEditMeta}
            />
          </FormCell>
        )}
        <FormCell $size={'lg'}>
          <FieldLabel>
            SQL Expression (use @field_key to reference another field)
          </FieldLabel>
          <ExpressionWrapper>
            <TextAreaField
              value={draft.rawSql.rawSql}
              onChange={setRawSql}
              placeholder={"Example: CONCAT(@field_key1, '-' , @field_key2)"}
              minRows={1}
              maxRows={16}
              hasError={isDirty && !!validationError}
            />
            <KubitTooltip
              content={
                'Select this only if your expression relies on window functions over the full result set. ' +
                'Selecting it otherwise will have negative performance impact.'
              }
            >
              <CheckboxWrapper onClick={onFullDataClick}>
                <TickCheckbox checked={draft.rawSql.fullDataRequired} />
                <FieldLabel>Window Functions</FieldLabel>
              </CheckboxWrapper>
            </KubitTooltip>
          </ExpressionWrapper>
        </FormCell>
        <PrimaryButton
          disabled={!canConfirm}
          onClick={handleConfirm}
          trackContext="data modeler"
          trackAction="derived field - confirm"
        >
          Confirm
        </PrimaryButton>
        <SecondaryButton
          isOutlined={true}
          onClick={removeNewDraftField}
          trackContext="data modeler"
          trackAction="derived field - cancel"
        >
          Cancel
        </SecondaryButton>
      </FormRow>
      {fieldNameError && (
        <FormRow>
          <FieldError type={'custom'} customError={fieldNameError} />
        </FormRow>
      )}
      {!fieldNameError && isDirty && !!validationError && (
        <FormRow>
          <FieldError type={'custom'} customError={validationError} />
        </FormRow>
      )}
    </FormWrapper>
  );
};

export default DataModelerEditDerivedField;
